import React, { useEffect, useMemo, useState } from 'react';
import {
  Check,
  Send,
  Share2,
  Shuffle,
  Sparkles,
  X,
} from 'lucide-react';
import './TechConnections.css';
import {
  formatPuzzleDate,
  getAlmostMatchedGroup,
  getGroupForWords,
  getLocalDateKey,
  getPuzzleForDate,
  getPuzzleWords,
  getTechConnectionsPuzzles,
  normalizePuzzleWord,
} from './techConnectionsPuzzles';

const MAX_MISTAKES = 4;
const STORAGE_PREFIX = 'tech-connections-state';

const hashString = (value) => {
  let hash = 2166136261;

  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
};

const seededRandom = (seed) => {
  let state = hashString(seed) || 1;

  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return ((state >>> 0) / 4294967296);
  };
};

const shuffleWithSeed = (items, seed) => {
  const next = [...items];
  const random = seededRandom(seed);

  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }

  return next;
};

const shuffleWithMathRandom = (items) => {
  const next = [...items];

  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }

  return next;
};

const makeInitialState = (puzzle) => ({
  selectedWords: [],
  solvedLevels: [],
  mistakes: [],
  guesses: [],
  status: 'playing',
  wordOrder: puzzle ? shuffleWithSeed(getPuzzleWords(puzzle), puzzle.id) : [],
});

const getStorageKey = (puzzleId) => `${STORAGE_PREFIX}:${puzzleId}`;

const getGuessKey = (words) => (
  words.map(normalizePuzzleWord).sort().join('|')
);

const getWordLevelMap = (puzzle) => {
  const wordLevelMap = {};

  puzzle.groups.forEach((group) => {
    group.words.forEach((word) => {
      wordLevelMap[normalizePuzzleWord(word)] = group.level;
    });
  });

  return wordLevelMap;
};

const getStoredGuessKey = (guess) => {
  if (typeof guess === 'string') return guess;
  if (guess?.key) return guess.key;
  if (Array.isArray(guess?.words)) return getGuessKey(guess.words);
  return '';
};

const buildGuessRecord = (words, result, level = null) => ({
  key: getGuessKey(words),
  words: words.map(normalizePuzzleWord),
  result,
  ...(level ? { level } : {}),
});

const buildGuessHistory = (puzzle, parsedState) => {
  if (Array.isArray(parsedState.guesses)) {
    return parsedState.guesses;
  }

  const solvedGuesses = (parsedState.solvedLevels || [])
    .map((level) => puzzle.groups.find((group) => group.level === level))
    .filter(Boolean)
    .map((group) => buildGuessRecord(group.words, 'hit', group.level));

  const missedGuesses = (parsedState.mistakes || [])
    .filter(Array.isArray)
    .map((words) => buildGuessRecord(words, 'miss'));

  return [...missedGuesses, ...solvedGuesses];
};

const loadStateForPuzzle = (puzzle) => {
  if (!puzzle || typeof window === 'undefined') return makeInitialState(puzzle);

  const storedState = window.localStorage.getItem(getStorageKey(puzzle.id));

  if (!storedState) return makeInitialState(puzzle);

  try {
    const parsedState = JSON.parse(storedState);
    const puzzleWords = new Set(getPuzzleWords(puzzle));
    const isStoredStateValid = Array.isArray(parsedState.wordOrder)
      && parsedState.wordOrder.length === 16
      && parsedState.wordOrder.every((word) => puzzleWords.has(normalizePuzzleWord(word)))
      && Array.isArray(parsedState.selectedWords)
      && Array.isArray(parsedState.solvedLevels)
      && Array.isArray(parsedState.mistakes)
      && ['playing', 'won', 'lost'].includes(parsedState.status);

    return isStoredStateValid
      ? { ...parsedState, guesses: buildGuessHistory(puzzle, parsedState) }
      : makeInitialState(puzzle);
  } catch (error) {
    window.localStorage.removeItem(getStorageKey(puzzle.id));
    return makeInitialState(puzzle);
  }
};

const getGroupClassName = (level) => `tc-solved-group tc-solved-${level}`;

const getGuessRows = (puzzle, state) => {
  const levelLetterMap = {
    yellow: 'Y',
    green: 'G',
    blue: 'B',
    purple: 'P',
  };
  const wordLevelMap = getWordLevelMap(puzzle);

  return (state.guesses || []).map((guess) => (
    (guess.words || []).map((word) => levelLetterMap[wordLevelMap[normalizePuzzleWord(word)]] || 'X').join('')
  ));
};

const getShareRows = (guessRows) => {
  const squareMap = {
    Y: '\u{1F7E8}',
    G: '\u{1F7E9}',
    B: '\u{1F7E6}',
    P: '\u{1F7EA}',
    X: '\u2B1B',
  };

  return guessRows.map((row) => (
    row.split('').map((letter) => squareMap[letter] || squareMap.X).join('')
  ));
};

const getShareUrl = (puzzle) => {
  if (!puzzle || typeof window === 'undefined') return '';

  const url = new URL('/connections', window.location.origin);
  url.searchParams.set('date', puzzle.date);
  return url.toString();
};

const getRequestedPuzzleDate = (puzzles) => {
  if (typeof window === 'undefined') return '';

  const requestedDate = new URLSearchParams(window.location.search).get('date');
  return puzzles.some((puzzle) => puzzle.date === requestedDate) ? requestedDate : '';
};

const getShareText = (puzzle, state, playUrl = '') => {
  const guessRows = getGuessRows(puzzle, state);
  const result = state.status === 'won' ? 'Solved' : 'Board revealed';

  return [
    `${puzzle.title} ${puzzle.date}`,
    `${result} in ${guessRows.length} guesses`,
    ...getShareRows(guessRows),
    `${MAX_MISTAKES - state.mistakes.length}/${MAX_MISTAKES} mistakes remaining`,
    playUrl ? `Play: ${playUrl}` : '',
  ].filter(Boolean).join('\n');
};

const TechConnections = () => {
  const puzzles = useMemo(() => getTechConnectionsPuzzles(), []);
  const todayKey = useMemo(() => getLocalDateKey(), []);
  const requestedDate = useMemo(() => getRequestedPuzzleDate(puzzles), [puzzles]);
  const initialPuzzle = useMemo(() => (
    puzzles.find((item) => item.date === requestedDate) || getPuzzleForDate(puzzles, todayKey)
  ), [puzzles, requestedDate, todayKey]);
  const puzzle = initialPuzzle || puzzles[0];
  const [hydratedPuzzleId, setHydratedPuzzleId] = useState(puzzle?.id);
  const [gameState, setGameState] = useState(() => loadStateForPuzzle(puzzle));
  const [notice, setNotice] = useState('');
  const [shareState, setShareState] = useState('idle');
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [tileFeedback, setTileFeedback] = useState(null);
  const [lastSolvedLevel, setLastSolvedLevel] = useState('');

  useEffect(() => {
    if (!puzzle) return;

    setGameState(loadStateForPuzzle(puzzle));
    setHydratedPuzzleId(puzzle.id);
    setNotice('');
    setShareState('idle');
    setTileFeedback(null);
    setLastSolvedLevel('');
  }, [puzzle]);

  useEffect(() => {
    if (!tileFeedback) return undefined;

    const timeoutId = window.setTimeout(() => setTileFeedback(null), 650);
    return () => window.clearTimeout(timeoutId);
  }, [tileFeedback]);

  useEffect(() => {
    if (!lastSolvedLevel) return undefined;

    const timeoutId = window.setTimeout(() => setLastSolvedLevel(''), 900);
    return () => window.clearTimeout(timeoutId);
  }, [lastSolvedLevel]);

  useEffect(() => {
    if (!puzzle || hydratedPuzzleId !== puzzle.id) return;
    window.localStorage.setItem(getStorageKey(puzzle.id), JSON.stringify(gameState));
  }, [gameState, hydratedPuzzleId, puzzle]);

  useEffect(() => {
    if (gameState.status === 'won' || gameState.status === 'lost') {
      setIsResultsOpen(true);
    }
  }, [gameState.status, puzzle.id]);

  const solvedGroups = useMemo(() => (
    gameState.solvedLevels
      .map((level) => puzzle.groups.find((group) => group.level === level))
      .filter(Boolean)
  ), [gameState.solvedLevels, puzzle.groups]);

  const isGameOver = gameState.status === 'won' || gameState.status === 'lost';
  const revealedGroups = useMemo(() => {
    if (!isGameOver) return [];
    return puzzle.groups.filter((group) => !gameState.solvedLevels.includes(group.level));
  }, [gameState.solvedLevels, isGameOver, puzzle.groups]);

  const solvedWordSet = useMemo(() => {
    const solvedWords = [...solvedGroups, ...revealedGroups]
      .flatMap((group) => group.words)
      .map(normalizePuzzleWord);
    return new Set(solvedWords);
  }, [revealedGroups, solvedGroups]);

  const activeWords = gameState.wordOrder.filter((word) => !solvedWordSet.has(normalizePuzzleWord(word)));
  const mistakesRemaining = MAX_MISTAKES - gameState.mistakes.length;
  const selectedGuessKey = gameState.selectedWords.length === 4 ? getGuessKey(gameState.selectedWords) : '';
  const hasAlreadyGuessedSelection = Boolean(selectedGuessKey && (
    gameState.guesses || []
  ).some((guess) => getStoredGuessKey(guess) === selectedGuessKey));
  const canSubmit = gameState.selectedWords.length === 4
    && gameState.status === 'playing'
    && !hasAlreadyGuessedSelection;
  const guessRows = useMemo(() => getGuessRows(puzzle, gameState), [gameState, puzzle]);
  const shareUrl = useMemo(() => getShareUrl(puzzle), [puzzle]);
  const toggleWord = (word) => {
    if (gameState.status !== 'playing') return;

    const normalizedWord = normalizePuzzleWord(word);
    const isSelected = gameState.selectedWords.includes(normalizedWord);

    if (!isSelected && gameState.selectedWords.length >= 4) {
      setNotice('Four selected.');
      return;
    }

    const nextSelectedWords = isSelected
      ? gameState.selectedWords.filter((selectedWord) => selectedWord !== normalizedWord)
      : [...gameState.selectedWords, normalizedWord];
    const nextGuessKey = nextSelectedWords.length === 4 ? getGuessKey(nextSelectedWords) : '';
    const nextAlreadyGuessed = Boolean(nextGuessKey && (
      gameState.guesses || []
    ).some((guess) => getStoredGuessKey(guess) === nextGuessKey));

    setNotice(nextAlreadyGuessed ? 'Already guessed!' : '');

    if (nextAlreadyGuessed) {
      setTileFeedback({
        key: `${nextGuessKey}-${Date.now()}`,
        type: 'duplicate',
        words: nextSelectedWords,
      });
    }

    setGameState((currentState) => ({
      ...currentState,
      selectedWords: nextSelectedWords,
    }));
  };

  const setGuessFeedback = (words, type) => {
    setTileFeedback({
      key: `${getGuessKey(words)}-${Date.now()}`,
      type,
      words,
    });
  };

  const clearSelection = () => {
    setNotice('');
    setGameState((currentState) => ({
      ...currentState,
      selectedWords: [],
    }));
  };

  const shuffleActiveWords = () => {
    if (gameState.status !== 'playing') return;

    const shuffledActiveWords = shuffleWithMathRandom(activeWords);
    const solvedWords = gameState.wordOrder.filter((word) => solvedWordSet.has(normalizePuzzleWord(word)));

    setGameState((currentState) => ({
      ...currentState,
      wordOrder: [...shuffledActiveWords, ...solvedWords],
    }));
  };

  const submitGuess = () => {
    if (!canSubmit) return;

    const guessKey = getGuessKey(gameState.selectedWords);
    const hasAlreadyGuessed = (gameState.guesses || []).some((guess) => getStoredGuessKey(guess) === guessKey);

    if (hasAlreadyGuessed) {
      setGuessFeedback(gameState.selectedWords, 'duplicate');
      setNotice('Already guessed!');
      return;
    }

    const matchedGroup = getGroupForWords(puzzle, gameState.selectedWords);

    if (matchedGroup) {
      const nextSolvedLevels = [...gameState.solvedLevels, matchedGroup.level];
      const hasWon = nextSolvedLevels.length === puzzle.groups.length;
      const nextGuesses = [
        ...(gameState.guesses || []),
        buildGuessRecord(gameState.selectedWords, 'hit', matchedGroup.level),
      ];

      setGameState((currentState) => ({
        ...currentState,
        selectedWords: [],
        solvedLevels: nextSolvedLevels,
        guesses: nextGuesses,
        status: hasWon ? 'won' : 'playing',
      }));
      setLastSolvedLevel(matchedGroup.level);
      setNotice(hasWon ? '' : 'Matched.');
      return;
    }

    const almostGroup = getAlmostMatchedGroup(puzzle, gameState.selectedWords, gameState.solvedLevels);
    const nextMistakes = [...gameState.mistakes, gameState.selectedWords];
    const nextGuesses = [
      ...(gameState.guesses || []),
      buildGuessRecord(gameState.selectedWords, almostGroup ? 'one-away' : 'miss'),
    ];
    const hasLost = nextMistakes.length >= MAX_MISTAKES;
    setGuessFeedback(gameState.selectedWords, almostGroup ? 'one-away' : 'miss');

    setGameState((currentState) => ({
      ...currentState,
      selectedWords: [],
      mistakes: nextMistakes,
      guesses: nextGuesses,
      status: hasLost ? 'lost' : 'playing',
    }));

    if (hasLost) {
      setNotice('Board revealed.');
    } else if (almostGroup) {
      setNotice('One away...');
    } else if (MAX_MISTAKES - nextMistakes.length === 1) {
      setNotice('Not a group. One mistake remaining.');
    } else {
      setNotice('Not a group.');
    }
  };

  const shareResult = async () => {
    const shareText = getShareText(puzzle, gameState, shareUrl);

    try {
      await navigator.clipboard.writeText(shareText);
      setShareState('copied');
      window.setTimeout(() => setShareState('idle'), 1600);
    } catch (error) {
      setShareState('blocked');
      window.setTimeout(() => setShareState('idle'), 2200);
    }
  };

  if (!puzzle) {
    return (
      <div className="tech-connections-page">
        <div className="tc-empty-state">No seeded boards found.</div>
      </div>
    );
  }

  return (
    <div className="tech-connections-page">
      <section className="tc-header-section">
        <div className="tc-title-block">
          <div className="tc-kicker">
            <Sparkles size={16} />
            Tech Connections
          </div>
          <h1>{puzzle.title}</h1>
          <span className="tc-date-line">{formatPuzzleDate(puzzle.date)}</span>
          <p>Create four groups of four!</p>
        </div>
      </section>

      <section className="tc-game-shell" aria-label="Tech Connections board">
        <div className="tc-solved-list">
          {solvedGroups.map((group) => (
            <div
              key={group.level}
              className={`${getGroupClassName(group.level)} ${group.level === lastSolvedLevel ? 'just-solved' : ''}`}
            >
              <strong>{group.category}</strong>
              <span>{group.words.join(', ')}</span>
            </div>
          ))}

          {revealedGroups.map((group) => (
            <div key={group.level} className={`${getGroupClassName(group.level)} revealed`}>
              <strong>{group.category}</strong>
              <span>{group.words.join(', ')}</span>
            </div>
          ))}
        </div>

        {activeWords.length > 0 && (
          <div className="tc-grid">
            {activeWords.map((word) => {
              const normalizedWord = normalizePuzzleWord(word);
              const selected = gameState.selectedWords.includes(normalizedWord);
              const feedbackType = tileFeedback?.words?.includes(normalizedWord) ? tileFeedback.type : '';

              return (
                <button
                  key={word}
                  type="button"
                  className={`tc-word-tile ${selected ? 'selected' : ''} ${feedbackType ? `feedback-${feedbackType}` : ''}`}
                  onClick={() => toggleWord(word)}
                  aria-pressed={selected}
                >
                  {word}
                </button>
              );
            })}
          </div>
        )}

        <div className="tc-notice-row" aria-live="polite">
          {notice && <span>{notice}</span>}
          {!notice && gameState.selectedWords.length > 0 && (
            <span>{gameState.selectedWords.length}/4 selected</span>
          )}
        </div>

        <div className="tc-mistakes-row" aria-label={`${mistakesRemaining} mistakes remaining`}>
          <span>Mistakes remaining:</span>
          <div>
            {Array.from({ length: MAX_MISTAKES }).map((_, index) => (
              <span
                key={index}
                className={`tc-mistake-dot ${index >= mistakesRemaining ? 'spent' : ''}`}
              />
            ))}
          </div>
        </div>

        <div className="tc-controls">
          <button type="button" className="tc-control-button" onClick={shuffleActiveWords} disabled={isGameOver}>
            <Shuffle size={18} />
            Shuffle
          </button>
          <button type="button" className="tc-control-button" onClick={clearSelection} disabled={gameState.selectedWords.length === 0}>
            <X size={18} />
            Deselect All
          </button>
          <button
            type="button"
            className="tc-control-button primary"
            onClick={submitGuess}
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
          >
            <Send size={18} />
            Submit
          </button>
        </div>

        {isGameOver && (
          <div className={`tc-end-state ${gameState.status}`}>
            <div>
              <Check size={20} />
              <strong>{gameState.status === 'won' ? 'Solved' : 'Board revealed'}</strong>
            </div>
            <div className="tc-end-actions">
              <button type="button" className="tc-control-button" onClick={() => setIsResultsOpen(true)}>
                Results
              </button>
              <button type="button" className="tc-control-button" onClick={shareResult}>
                <Share2 size={18} />
                {shareState === 'copied' ? 'Copied' : 'Share'}
              </button>
            </div>
            {shareState === 'blocked' && <span className="tc-share-fallback">{getShareText(puzzle, gameState, shareUrl)}</span>}
          </div>
        )}

        {isGameOver && isResultsOpen && (
          <div className="tc-modal-backdrop" role="presentation">
            <div className="tc-results-modal" role="dialog" aria-modal="true" aria-labelledby="tc-results-title">
              <button
                type="button"
                className="tc-modal-close"
                onClick={() => setIsResultsOpen(false)}
                aria-label="Close results"
              >
                <X size={20} />
              </button>

              <div className="tc-results-header">
                <span className={`tc-results-badge ${gameState.status}`}>
                  {gameState.status === 'won' ? 'Solved' : 'Revealed'}
                </span>
                <h2 id="tc-results-title">{puzzle.title}</h2>
                <p>{gameState.guesses.length} guesses, {mistakesRemaining} mistakes remaining</p>
              </div>

              <div className="tc-guess-grid" aria-label="Guess history">
                {guessRows.map((row, rowIndex) => (
                  <div className="tc-guess-row" key={`${row}-${rowIndex}`}>
                    {row.split('').map((letter, letterIndex) => (
                      <span
                        key={`${letter}-${letterIndex}`}
                        aria-label={`Guess ${rowIndex + 1} ${letter}`}
                        className={`tc-guess-square tc-guess-${letter.toLowerCase()}`}
                        style={{ animationDelay: `${(rowIndex * 50) + (letterIndex * 35)}ms` }}
                      />
                    ))}
                  </div>
                ))}
              </div>

              <div className="tc-results-actions">
                <button type="button" className="tc-control-button primary" onClick={shareResult}>
                  <Share2 size={18} />
                  {shareState === 'copied' ? 'Copied' : 'Share'}
                </button>
              </div>

              {shareState === 'blocked' && <span className="tc-share-fallback">{getShareText(puzzle, gameState, shareUrl)}</span>}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default TechConnections;
export { getGuessKey, getGuessRows, getShareRows, getShareText };
